import useGetExtensionInfo from '@hooks/useGetExtensionInfo';
import { useEffect } from 'react';
import {
  useAnimatedStyle,
  useSharedValue,
  withDelay,
  withTiming,
} from 'react-native-reanimated';

type Props = {
  startingWidth: number;
  containerWidth: number;
};

const ANIMATION_DURATION = 500;

export default function useExtensionProgressBarAnimations({
  startingWidth,
  containerWidth,
}: Props) {
  const { totalPrice = 1, currentCommitment = 0 } = useGetExtensionInfo();
  const progress = currentCommitment / totalPrice;
  const progressWidth =
    startingWidth + progress * (containerWidth - 2 * startingWidth);

  const animatedProgressBarWidth = useSharedValue(startingWidth);
  const animatedTextOpacityLeft = useSharedValue(0);
  const animatedTextOpacityRight = useSharedValue(0);

  const animatedWidthStyle = useAnimatedStyle(() => ({
    width: animatedProgressBarWidth.value + startingWidth,
  }));
  const animatedTextLeftStyle = useAnimatedStyle(() => ({
    opacity: animatedTextOpacityLeft.value,
  }));
  const animatedTextRightStyle = useAnimatedStyle(() => ({
    opacity: animatedTextOpacityRight.value,
  }));

  useEffect(() => {
    animatedProgressBarWidth.value = withTiming(progressWidth, {
      duration: ANIMATION_DURATION,
    });

    if (progress > 0.5) {
      animatedTextOpacityLeft.value = withDelay(
        ANIMATION_DURATION,
        withTiming(1, { duration: ANIMATION_DURATION }),
      );
      animatedTextOpacityRight.value = withTiming(0);
    } else {
      animatedTextOpacityLeft.value = withTiming(0);
      animatedTextOpacityRight.value = withDelay(
        ANIMATION_DURATION,
        withTiming(1, { duration: ANIMATION_DURATION }),
      );
    }
  }, [progressWidth, progress]);

  return {
    animatedWidthStyle,
    animatedTextLeftStyle,
    animatedTextRightStyle,
  };
}
